import React from 'react';
import {Glob as G} from "../../libs/Glob.mjs";
import EditorEmptyField from "./EditorEmptyField.mjs";
import EditorTextField from "./EditorTextField.mjs";
import EditorLabelOnlyField from "./EditorLabelOnlyField.mjs";
import EditorRadioField from "./EditorRadioField.mjs";
import EditorSelectField from "./EditorSelectField.mjs";
import EditorUnitValue from "./EditorUnitValue.mjs";
import EditorUnknownField from "./EditorUnknownField.mjs";
import {fieldTypes,cleanFieldsForCommit} from "./EditForm.mjs";
import AddFieldBar from "./AddFieldBar.mjs";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import ListIcon from "../../media/images/ListIcon.mjs";
import TextLabel from "../../controls/TextLabel.mjs";
import EditorCheckboxField from "./EditorCheckboxField.mjs";
import EditorGroup from "./EditorGroup.mjs";

const prefix = "EditorFieldGroup"

const EditorFieldGroup = class extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			fieldInfo: props.field ? G.clone(props.field) : {},
			subfields: props.fields ? G.clone(props.fields) : [],
			fieldKeys: props.fieldKeys
		};
		this.uid = G.generateUid(prefix)
		this.commit = this.commit.bind(this);
		this.commitUp = this.commitUp.bind(this);
		this.renderField = this.renderField.bind(this);
		this.swap = this.swap.bind(this);
		this.moveTop = this.moveTop.bind(this);
		this.moveBottom = this.moveBottom.bind(this);
	}

	/**
	 * Swaps two field definitions by their indexes.
	 * @private
	 * @function
	 * @param {number} from Index of the first item
	 * @param {number} to Index of the second item
	 * @return {void}
	 */
	swap(from, to ) {
		/** @type { FieldDefinition[] } */
		const v = G.clone( this.state.subfields );
		/** @type { FieldDefinition } */
		const a = G.clone(v[from]);
		v[from] = G.clone(v[to]);
		v[to] = a;
		this.commitUp(v);
	}

	/**
	 * Move a field to the bottom of list
	 * @private
	 * @function
	 * @param {number} from The index of the field to be moved
	 * @return {void}
	 */
	moveBottom( from ) {
		/** @type { FieldDefinition } */
		const a = G.clone(this.state.subfields[from]);
		/** @type { FieldDefinition[] } */
		const v = G.clone( this.state.subfields );
		v.splice(from, 1);
		v.push(a);
		this.commitUp(v);
	}

	/**
	 * Move a field to top of the list
	 * @private
	 * @function
	 * @param {number} from The index of the field to be moved
	 * @return {void}
	 */
	moveTop( from ) {
		/** @type { FieldDefinition } */
		const a = G.clone(this.state.subfields[from]);
		/** @type { FieldDefinition[] } */
		const v = G.clone( this.state.subfields );
		v.splice(from, 1);
		v.unshift(a);
		this.commitUp(v);
	}

	commitUp( fields ) {
		if ( Array.isArray(fields) ) {
			this.setState({ subfields: fields } );
		} else {
			fields = this.state.subfields;
		}
		this.props.commit(fields);
	}

	/**
	 * - Commit changes in "fdef" object at index "idx"
	 * - Appends "fdef" if index "idx" is undefined.
	 * - Remove field index "idx" if "fdef" is undefined.
	 * @private
	 * @function
	 * @param { FieldDefinition } [fDef] Field definition object, undefined to delete it
	 * @param { number } [index] Field definition number, undefined to append it
	 * @return {void}
	 */
	commit( fDef, index) {
		let isInsert= this.state.insertAtIndex;
		const f = this.state.subfields || [];
		const nextState = {};
		if ( typeof fDef === "object" ) {
			if ( typeof index !== "number" ) {
				if ( typeof isInsert !== 'number') isInsert = f.length;
				index = isInsert -1;
			}
			if ( typeof isInsert === 'number' ) {
				nextState.insertAtIndex = undefined;
				f.splice( isInsert, 0, fDef);
			} else {
				f[index] = fDef;
			}
		} else if ( typeof index === "number" ) {
			f.splice(index, 1);
		}
		cleanFieldsForCommit(f, nextState).then( ns => {
			this.setState(ns, this.commitUp );
		})
	}

	/**
	 * @function
	 * @param {FieldDefinition} fn
	 * @param {number} idx
	 * @return {JSX.Element}
	 */
	renderField( fn, idx ) {
		const type = fn?.type || 'format';
		const maxIndex =  this.state.subfields.length -1;
		const props = {
			key: `${idx}-${fn.dependants?.length||0}`,
			/** Current value of field */
			value: fn,
			/** Current index of field */
			index: idx,
			/**
			 * Global list of used field keys
			 * @type { string[] }
			 */
			fieldKeys: this.state.fieldKeys,
			/**
			 * Commit field changes
			 * @param {Object} [fDef] Field definition, undefined to delete
			 */
			commit: fDef => { this.commit(fDef, idx ) },
			moveUp: idx ? ()=> { this.swap( idx, idx -1) } : undefined,
			moveDown: idx < maxIndex ? ()=> { this.swap( idx, idx +1) } : undefined,
			moveTop: idx ? ()=> { this.moveTop( idx ) } : undefined,
			moveBottom: idx < maxIndex ? ()=> { this.moveBottom( idx ) } : undefined,
			fieldTypes: fieldTypes,
			insertAfter: ()=>{ this.setState({ insertAtIndex: idx +1 } ) },
		}
		if ( type === undefined ) console.log( fn );
		switch ( type ) {
			case 'format': return <EditorEmptyField {...props} />;
			case 'text': return <EditorTextField {...props} />;
			case 'YN': return <EditorLabelOnlyField {...props} />;
			case 'YNA': return <EditorLabelOnlyField {...props} />;
			case 'radio': return <EditorRadioField {...props} />;
			case 'checkbox': return <EditorCheckboxField {...props} />;
			case 'select': return <EditorSelectField {...props} />;
			case 'TimeAmount': return <EditorLabelOnlyField {...props} />;
			case 'UnitValue': return <EditorUnitValue {...props} />;
			case 'percent': return <EditorLabelOnlyField {...props} />;
			case 'iteminfo': return <EditorEmptyField {...props} />;
			case 'group': return <EditorGroup {...props} />;
			case 'ateco': return <EditorLabelOnlyField {...props} />;
			default: return <EditorUnknownField {...props} />;
		}
	}

	render() {
		return(
			<div className={prefix + ( this.props.key ? ' indent' : '')}>
				{ this.state.subfields.length > 0 ? (
					this.state.subfields.map((f, idx) => this.renderField(f, idx))
				) : (
					<div className={"field"}>
						<AddFieldBar
							label={G.label('Add')}
							key={(this.state.subfields || []).length}
							fields={this.state.subfields || []}
							fieldTypes={fieldTypes}
							commit={this.commit}
						/>
					</div>
				)}
				<Modal
					isOpen={!!this.state.insertAtIndex}
				>
					<ModalHeader
						toggle={() => {
							this.setState({insertAtIndex: undefined})
						}}
					>
						<ListIcon/>
						<TextLabel value={"Add"}/>
					</ModalHeader>
					<ModalBody>
						<AddFieldBar
							key={ (this.state.subfields || []).length }
							fields={ this.state.subfields || [] }
							fieldTypes={ fieldTypes }
							commit={this.commit}
						/>
					</ModalBody>
				</Modal>
			</div>
		)
	}
}

export default EditorFieldGroup;